// Conversation logic for the floating chat bot.
//
// The bot walks one question per filter dimension, in the order the backend
// serves them (api/chatbot/filters), and only ever accepts an answer that is one
// of that dimension's options. Everything here is pure — the store owns the HTTP
// and the state, the widget owns the rendering — so the matching rules can be
// reasoned about (and reused by the results page) without a component instance.

// Words that move the conversation along instead of answering it. Kept apart
// from the option list on purpose: a filter option is never one of these, so a
// control word can be recognised before matching and cannot be shadowed by a
// city or status that happens to share its spelling.
const SKIP_WORDS = ["skip", "any", "all", "no preference", "none", "next"];
const DONE_WORDS = ["done", "that's it", "thats it", "finish", "go", "search", "run"];
const BACK_WORDS = ["back", "undo", "previous"];

export const CONTROL = {
  SKIP: "skip",
  DONE: "done",
  BACK: "back",
};

const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();

// The control word a message is, or null if it is meant as an answer.
export const controlWord = (message) => {
  const m = norm(message);
  if (!m) return null;
  if (SKIP_WORDS.includes(m)) return CONTROL.SKIP;
  if (DONE_WORDS.includes(m)) return CONTROL.DONE;
  if (BACK_WORDS.includes(m)) return CONTROL.BACK;
  return null;
};

// Resolves one typed value against a question's options.
//
// Returns { status, value, candidates }:
//   'ok'        - value is the canonical option label
//   'ambiguous' - candidates holds every option the text could mean
//   'rejected'  - the text is not inside this filter
//
// Tried in order of confidence — exact, then prefix, then substring — so a typed
// "Now Selling" lands on that status rather than being called ambiguous against
// "Now Selling 2", which contains it.
export const matchOption = (text, options) => {
  const needle = norm(text);
  if (!needle) return { status: "rejected", candidates: [] };

  const exact = (options || []).filter((o) => norm(o) === needle);
  if (exact.length) return { status: "ok", value: exact[0], candidates: [] };

  const prefix = (options || []).filter((o) => norm(o).startsWith(needle));
  if (prefix.length === 1) return { status: "ok", value: prefix[0], candidates: [] };
  if (prefix.length > 1) return { status: "ambiguous", candidates: prefix };

  const partial = (options || []).filter((o) => norm(o).includes(needle));
  if (partial.length === 1) return { status: "ok", value: partial[0], candidates: [] };
  if (partial.length > 1) return { status: "ambiguous", candidates: partial };

  return { status: "rejected", candidates: [] };
};

// Splits a typed message into the values it offers. Commas separate answers, so
// "Burnaby, Surrey" is two — but only where the message is not itself a single
// option, which is what protects the options that contain a comma of their own.
export const splitAnswers = (message, options) => {
  const whole = matchOption(message, options);
  if (whole.status === "ok") return [message];
  return String(message)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

// Resolves a whole message against a question.
//
// Returns { accepted, rejected, ambiguous } — accepted holds canonical labels,
// rejected and ambiguous hold notes the bot repeats back. Nothing is guessed:
// a value the filter does not contain is reported, never coerced to the nearest
// option, because a silently-substituted answer would query for something the
// user did not ask for.
export const resolveAnswer = (message, question) => {
  const options = (question && question.options) || [];
  const accepted = [];
  const rejected = [];
  const ambiguous = [];

  for (const raw of splitAnswers(message, options)) {
    const match = matchOption(raw, options);
    if (match.status === "ok") {
      if (!accepted.includes(match.value)) accepted.push(match.value);
    } else if (match.status === "ambiguous") {
      ambiguous.push({ input: raw, candidates: match.candidates.slice(0, 8) });
    } else {
      rejected.push(raw);
    }
  }

  // A single-answer question keeps the first value; the rest are reported so the
  // user can see the bot did not quietly widen their filter.
  if (question && !question.multiple && accepted.length > 1) {
    const dropped = accepted.splice(1);
    rejected.push(...dropped);
  }

  return { accepted, rejected, ambiguous };
};

// What the bot says when it will not take a value. Names the filter and shows a
// handful of what it would take, so the correction is one click away.
export const rejectionMessage = (question, rejected, ambiguous) => {
  const lines = [];
  const label = (question && question.label) || "that filter";

  for (const item of ambiguous) {
    lines.push(
      `“${item.input}” could mean ${item.candidates.join(", ")} — which one?`
    );
  }
  if (rejected.length) {
    const listed = rejected.map((r) => `“${r}”`).join(", ");
    lines.push(
      `${listed} ${rejected.length > 1 ? "are not" : "is not"} a ${label} option. Pick one of the choices below, or say “skip”.`
    );
  }
  return lines.join(" ");
};

// One-line recap of everything answered so far, for the summary card and the PDF
// header. Skipped questions are absent rather than shown as "any" — the query
// carries no predicate for them at all.
export const summariseAnswers = (answers, questions) =>
  (questions || [])
    .filter((q) => (answers[q.key] || []).length)
    .map((q) => ({
      key: q.key,
      label: q.label,
      values: answers[q.key],
      text: `${q.label}: ${answers[q.key].join(", ")}`,
    }));

// Result columns. One definition drives the Tabulator table, the CSV and the
// PDF, so a column added here reaches all three.
export const RESULT_COLUMNS = [
  { field: "project", title: "Project", width: 240 },
  { field: "address", title: "Address", width: 220 },
  { field: "city", title: "City", width: 140 },
  { field: "status", title: "Status", width: 140 },
  { field: "unit_type", title: "Unit Type", width: 180 },
  { field: "building_type", title: "Building Type", width: 170 },
  { field: "construction_type", title: "Construction Type", width: 160 },
  { field: "construction_status", title: "Construction Status", width: 170 },
  { field: "total_units", title: "Total Units", width: 110, numeric: true },
];

// Cell value for export. Nulls become an em dash so a blank cell reads as
// "no value" rather than as a formatting slip, and unit counts lose their
// stray decimals.
export const exportValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const column = RESULT_COLUMNS.find((c) => c.field === field);
  if (column && column.numeric) {
    const n = Number(value);
    return isNaN(n) ? String(value) : Math.round(n).toLocaleString("en-CA");
  }
  return String(value);
};
